import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Box, TextField, Grid, Button, Link, Alert } from "@mui/material";
import { LanguageItem, AuthPageLayout } from "../../lib/common";
import AsyncFormBox from "../../lib/common/AsyncFormBox";
import { useSession, usePasswordValidation } from "../../lib/hooks";
import { CommonCalls } from "../../config/calls";
import { UsernameInput } from "../../lib/inputs";
import BirthDataInput from "../Registration/Wizard/inputs/BirthDataInput";
import { WIZARD_MODES } from "../Registration/Shared/WizardModes";
import { extractUserMetadata } from "../../utils/RegistrationUtils";

const IS_EMAIL_FLOW = import.meta.env.VITE_REGISTRATION_FLOW === "email";

function BackToSignInLink() {
  return (
    <Link component={RouterLink} to={"/signIn"} variant={"body2"}>
      <LanguageItem cs>Již máte účet? Přihlašte se</LanguageItem>
      {/* <LanguageItem en>Already have an account? Sign in</LanguageItem> */}
    </Link>
  );
}

function RegistrationSentFeedback() {
  return (
    <Box sx={{ mt: 3, maxWidth: 400, textAlign: "center" }}>
      <Alert severity="success" sx={{ mb: 2 }}>
        <LanguageItem cs>
          Na váš e-mail byl odeslán odkaz pro ověření. Klikněte na něj pro dokončení registrace.
        </LanguageItem>
        {/* <LanguageItem en>
          A verification link has been sent to your email. Click it to complete your registration.
        </LanguageItem> */}
      </Alert>
      <BackToSignInLink />
    </Box>
  );
}

/**
 * Returns translated message for known registration error codes.
 * @param {object} error - Full error object (Axios error with response.data).
 * @returns {string|undefined} Translated message or undefined for unknown errors.
 */
function getRegistrationErrorMessage(error) {
  console.log("error", error);
  const data = error?.response?.data;
  const code = data?.code;
  const causeName = data?.params?.name;
  const causeParams = data?.params?.params;
  if (code === "glint-js/registrationFailed" && causeName === "UserExistsError") {
    return "Uživatel se zvoleným e-mailem je již registrovaný.";
  } else if (causeParams?.keyPattern?.["metadata.birthNumber"]) {
    return "Uživatel se zvoleným rodným číslem je již registrovaný.";
  }
}

function SignUp() {
  const session = useSession();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [userError, setUserError] = useState();
  const { validate, passwordFieldProps, confirmPasswordFieldProps } = usePasswordValidation();

  /**
   * Submits the registration form to the backend.
   * In email flow, calls CommonCalls.register directly to avoid session auto-login.
   * In basic flow, calls session.register which auto-authenticates on success.
   * @param {object} values - Form values.
   * @returns {Promise} API call promise.
   */
  function handleSubmit(values) {
    const metadata = extractUserMetadata(values, "personalData");

    // dtoIn must match glint-js UserRegisterSchema (see cascades register-test-users, faker-helpers).
    const body = {
      username: values.email,
      email: values.email,
      password: values.password,
      confirmPassword: values.confirmPassword,
      firstName: values.firstName,
      lastName: values.lastName,
      language: "cs",
    };

    if (Object.keys(metadata).length > 0) {
      body.metadata = metadata;
    }

    if (IS_EMAIL_FLOW) {
      body.hostUri = window.location.origin;
      return CommonCalls.register(body);
    }

    return session.register(body);
  }

  /**
   * Handles successful registration.
   * Email flow: shows "check your email" view.
   * Basic flow: navigates to home (user is auto-authenticated).
   * @param {object} result - API call result.
   */
  function setRegistrationUserError(error) {
    const message = getRegistrationErrorMessage(error);
    setUserError({
      title: (
        <>
          <LanguageItem cs>Registrace se nezdařila</LanguageItem>
          {/* <LanguageItem en>Sign In has failed</LanguageItem> */}
        </>
      ),
      message: (
        <>
          <LanguageItem cs>{message ?? "Kontaktujte správce aplikace."}</LanguageItem>
          {/* <LanguageItem en>{message ?? "Contact administrator of the application."}</LanguageItem> */}
        </>
      ),
    });
  }

  /**
   * Used by AsyncFormBox for thrown errors (email flow). Returns fragment to suppress default ErrorBox.
   * userError is set via onSubmitFail (called in catch block, not during render) to avoid infinite loop.
   * @param {object} error - Full error object (Axios error).
   * @returns {JSX.Element} Empty fragment.
   */
  function translateError() {
    return <></>;
  }

  function onSubmitDone(result) {
    if (result instanceof Error) {
      setRegistrationUserError(result);
      return;
    }

    if (IS_EMAIL_FLOW) {
      setSubmitted(true);
      return;
    }

    // basic flow - session.register returns through _wrapCall
    navigate("/");
  }

  /**
   * Validates password requirements and confirmation match.
   * @param {object} values - Form values.
   * @returns {boolean} True if validation passes.
   */
  function validateInput(values) {
    return validate(values.password, values.confirmPassword);
  }

  return (
    <AuthPageLayout
      title={
        <>
          <LanguageItem cs>Registrace</LanguageItem>
          {/* <LanguageItem en>Sign Up</LanguageItem> */}
        </>
      }>
      {submitted && <RegistrationSentFeedback />}

      {!submitted && (
        <AsyncFormBox
          onValidate={validateInput}
          onSubmit={handleSubmit}
          onSubmitDone={onSubmitDone}
          onSubmitFail={setRegistrationUserError}
          translateError={translateError}
          userError={userError}
          boxProps={{
            sx: { mt: 2 },
          }}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}>
              <TextField
                autoComplete={"given-name"}
                name={"firstName"}
                required
                fullWidth
                label={
                  <>
                    <LanguageItem cs>Jméno</LanguageItem>
                    {/* <LanguageItem en>First Name</LanguageItem> */}
                  </>
                }
                autoFocus
                size="small"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}>
              <TextField
                required
                fullWidth
                label={
                  <>
                    <LanguageItem cs>Příjmení</LanguageItem>
                    {/* <LanguageItem en>Last Name</LanguageItem> */}
                  </>
                }
                name={"lastName"}
                autoComplete={"family-name"}
                size="small"
              />
            </Grid>
            <BirthDataInput mode={WIZARD_MODES.NEW} />
            <Grid size={12}>
              <UsernameInput fullWidth size="small" />
            </Grid>
            <Grid size={12}>
              <TextField
                {...passwordFieldProps}
                required
                fullWidth
                name={"password"}
                label={
                  <>
                    <LanguageItem cs>Heslo</LanguageItem>
                    {/* <LanguageItem en>Password</LanguageItem> */}
                  </>
                }
                type={"password"}
                autoComplete={"new-password"}
                size="small"
              />
            </Grid>
            <Grid size={12}>
              <TextField
                {...confirmPasswordFieldProps}
                required
                fullWidth
                name={"confirmPassword"}
                label={
                  <>
                    <LanguageItem cs>Potvrďte heslo</LanguageItem>
                    {/* <LanguageItem en>Confirm the password</LanguageItem> */}
                  </>
                }
                type={"password"}
                autoComplete={"new-password"}
                size="small"
              />
            </Grid>
          </Grid>

          <Button type={"submit"} fullWidth variant={"contained"} sx={{ mt: 3, mb: 2 }}>
            <LanguageItem cs>Registrovat</LanguageItem>
            {/* <LanguageItem en>Sign Up</LanguageItem> */}
          </Button>

          <Grid container justifyContent={"flex-end"}>
            <Grid>
              <BackToSignInLink />
            </Grid>
          </Grid>
        </AsyncFormBox>
      )}
    </AuthPageLayout>
  );
}

export default SignUp;
